import { useState, useEffect } from 'react';
import { useProfile } from '@/hooks';
import { getCustomerState, type PolarCustomerState } from '@/api/polar';

/**
 * Custom hook to fetch and manage Polar customer state
 * Fetches once on mount and provides customer data including credits
 */
export function useCustomerState() {
  const { profile } = useProfile();
  const [customerState, setCustomerState] = useState<PolarCustomerState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!profile?.user_id || profile?.subscription_status === "onboarding") {
      setIsLoading(false);
      return;
    }

    const fetchCustomerState = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const state = await getCustomerState(profile.user_id);
        setCustomerState(state);
      } catch (err) {
        console.error('Error fetching customer state:', err);
        setError(err instanceof Error ? err : new Error('Failed to fetch customer state'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchCustomerState();
  }, [profile?.user_id]);

  // Calculate total credits from active meters

  const totalCredits = customerState?.activeMeters?.[0].balance;

  // Get subscription info
  const activeSubscription = customerState?.activeSubscriptions?.[0] ?? null;

  // Map product ID to tier
  const getTier = (productId: string): string => {
    const productTierMap: Record<string, string> = {
      "4e8e2ce6-1f6f-41a2-a233-64447c5441d4": "weekly",
      "4ecdfad9-ed9f-464d-b66f-93ef90e3ac83": "solo",
      "814de567-163b-48a4-afae-4cb8a91d21ba": "pro",
      "8a798a28-22d0-4443-9916-7d13a9579edc": "pro_byok",
    };
    return productTierMap[productId] || "solo";
  };

  const subscriptionTier = activeSubscription
    ? getTier(activeSubscription.productId)
    : null;

  return {
    customerState,
    isLoading,
    error,
    totalCredits,
    activeSubscription,
    subscriptionTier,
    subscriptionStatus: activeSubscription?.status ?? null,
  };
}
